import { useCallback } from 'react';

import { OnAgentThinking } from '@/components/core';
import { AgentChatType, CHAT_CONFIG } from '@/constant/chatConfig';
import { useAgentChatCommon } from '@/hooks/useAgentChatCommon';
import { useChatSessionHistory } from '@/hooks/useChatSessionHistory';
import { useAgents } from '@/store/agents';
import { ChatType, useChats } from '@/store/chats';
import { useSendChatMessage } from '@/store/sessions';
import { User } from '@/types/auth';
import { ChatMessage, Session } from '@/types/chat';

type ChatProps = {
  chatType: AgentChatType;
  entityId?: string;
  sessionId?: string;
  user: User | null;
  sessions: Session[];
  isSessionsMode?: boolean;
  onMessage?: (message: ChatMessage) => void;
  onThinking?: OnAgentThinking;
  onSessionChangeClick?: (session: Session) => void;
  onReturnToChatClick?: () => void;
};

// Orchestrates agent chat: session resolution, history, and send pipeline.
const useAgentChat = ({
  chatType,
  entityId,
  sessionId,
  user,
  onMessage,
  onThinking,
  sessions,
  isSessionsMode,
  onSessionChangeClick,
  onReturnToChatClick,
}: ChatProps) => {
  const { isNewSession, sessionId: storedSessionId } = useChats(
    state => state.chatState[chatType]
  );
  const contactId = useChats(state =>
    chatType === ChatType.job ? state.chatState.job.contactId : undefined
  );
  const updateChatState = useChats(state => state.updateChatState);

  const { data: agent } = useAgents(CHAT_CONFIG[chatType].agentName);

  const { mutateAsync: sendChatRequest } = useSendChatMessage(
    CHAT_CONFIG[chatType].sendMessage
  );

  const resolvedSessionId = storedSessionId ?? sessionId;
  const { data: sessionHistory, refetch: refreshSessionHistory } =
    useChatSessionHistory(chatType, resolvedSessionId, !isSessionsMode);

  const sendChatMessage = useCallback(
    (message: string) => {
      const payload = CHAT_CONFIG[chatType].buildSendPayload({
        message,
        entityId,
        contactId: contactId ?? undefined,
        sessionId: resolvedSessionId,
      });

      return sendChatRequest(payload);
    },
    [chatType, sendChatRequest, entityId, contactId, resolvedSessionId]
  );

  const handleUpdateChatState = useCallback(
    (state: { isNewSession?: boolean; sessionId?: string | null }) =>
      updateChatState(chatType, state),
    [chatType, updateChatState]
  );

  const {
    handleLastMessageProcessCompleted,
    isChatActive,
    isLoading,
    sendMessage,
    messages,
    messagesEndRef,
    setMessages,
    updateMessageRating,
    setIsLoading,
    scrollToBottom,
  } = useAgentChatCommon({
    agent,
    sessionId: resolvedSessionId,
    isNewSession,
    onThinking,
    onMessage,
    sessionHistory,
    sendChatMessage,
    updateChatState: handleUpdateChatState,
    refreshSessionHistory,
  });

  return {
    messages,
    isLoading,
    sendMessage,
    scrollToBottom,
    messagesEndRef,
    setMessages,
    setIsLoading,
    sessions,
    isSessionsMode,
    onSessionChangeClick,
    onReturnToChatClick,
    handleLastMessageProcessCompleted,
    sessionId: resolvedSessionId,
    updateMessageRating,
    user,
    isChatActive,
    aiAgent: agent,
    isNewSession,
  };
};

export { useAgentChat };
